import { useEffect } from 'react';
import { RequestsPanel } from '../Requests';
import { Chores } from './Chores';
import { Inbox } from './Inbox';
import { Plan } from './Plan';
import { SubTabs, SubPanel, type SubTabDef } from '../../components/SubTabs';
import { Tooltip } from '../../ui/Tooltip';
import './requests.css';
import './chores.css';

/** Deep-link intent from the compose menu (design v1.1 §5.2): Ask opens the
 *  request composer. */
export interface LifeComposeIntent {
  kind: 'ask';
}

/**
 * Life's sub-navigation (card sjFxIbWi3UgN, Jonathan 2026-10-07): the same
 * top row Notebook carries, so a screen's sections are tabs, not one long
 * stack. Budget is deliberately NOT a tab yet — there is nothing behind it,
 * and a tab that opens prose about itself is the dead end this replaces; the
 * quiet line at the bottom keeps the promise until the section exists.
 */
/* Jonathan 2026-10-24 (email msg_c980c04af2cc45bba8e2b3673b3b4600, card
   fGcB7X75adxv): the order is Inbox / Plan / Chores / Requests, and the app
   LANDS on Inbox. This supersedes card 7aTCRQ9j8PWV (Plan fourth, Requests
   the landing tab) and sjFxIbWi3UgN's default. */
const LIFE_TABS: SubTabDef[] = [
  { key: 'admin', label: 'Inbox' },
  { key: 'plan', label: 'Plan' },
  { key: 'chores', label: 'Chores' },
  { key: 'requests', label: 'Requests' },
];

/**
 * Life panel — "Run the household". "Needs you" shows the LIFE lane only and
 * hides closed (done) rows: finished work is not something you still need to
 * do. Snoozed asks stay visible with their return date so a deferred ask is
 * never silently lost (card DlK33bskwmPe). Budget / inbox / calendar are
 * promised as one quiet prose line, not as dead-end tiles (TFeLwOAS1EeP).
 *
 * The tab state lives in App, not here: the panel unmounts every time the
 * bottom bar switches away, so keeping it locally would drop you back on
 * Requests every visit — and the More menu's "Needs you" quick link has to be
 * able to select the tab before it scrolls to it.
 *
 * Mirrors §5.5 of the design doc.
 */
export function LifePanel({
  intent,
  activeTab,
  onTabChange,
  members,
}: {
  intent?: LifeComposeIntent | null;
  activeTab: string;
  onTabChange: (key: string) => void;
  /** Needed by the Inbox tab: a member id reads as a display name on the routed-to row. */
  members: { memberId: string; displayName: string }[];
}) {
  // A compose deep-link targets the Requests lane, which is the default tab —
  // but not if you had switched to Chores before pressing +. Select it rather
  // than silently dropping the intent.
  useEffect(() => {
    if (intent?.kind === 'ask' && activeTab !== 'requests') onTabChange('requests');
  }, [intent, activeTab, onTabChange]);

  return (
    <>
      <SubTabs
        name="life"
        label="Life sections"
        tabs={LIFE_TABS}
        active={activeTab}
        onChange={onTabChange}
        sticky
      />
      <SubPanel name="life" tabKey={activeTab}>

        {activeTab === 'requests' && (
          <>
            {/* Jonathan 2026-10-24 (card ifd8sEdMo0JA): the first-move paragraph
                is gone from the layout — it rendered above the lane even when
                the lane was empty and pushed the user's own requests down the
                screen. What the paragraph used to explain now lives in a (?)
                tooltip beside the heading, so nothing here takes a line unless
                it is asked for. */}
            <h2 className="section-title" style={{ fontFamily: 'var(--font-heading)', fontSize: 'var(--type-h3-size)', letterSpacing: 'var(--type-tracking-wider)', fontWeight: 400, color: 'var(--color-cream)', marginTop: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              Requests
              <Tooltip label="Use Requests to ask a favour of your partner, where they can incorporate into their plans.">
                <span className="pear-tip-trigger" title="Use Requests to ask a favour of your partner, where they can incorporate into their plans.">ⓘ</span>
              </Tooltip>
            </h2>
            <div id="life-lane" className="life-lane">
              <RequestsPanel
                intent={intent?.kind === 'ask' ? { kind: 'ask' } : null}
                lane="life"
                hideDone
              />
            </div>
          </>
        )}

        {activeTab === 'chores' && <Chores />}

        {/* The couple's inbox, third sub-tab (card tlwgXSkiimS2, renamed from
            'Admin' by card NWmSqIsojQyU) — the mail Pear took in with the class
            and the routing reason, next to the work it generated instead of
            behind a More-menu screen. */}
        {activeTab === 'admin' && <Inbox members={members} />}

        {/* Plan (card 7aTCRQ9j8PWV): the weekly meeting — record, draft, confirm. */}
        {activeTab === 'plan' && <Plan />}

        {/* Budget / inbox / calendar still arrive later — said once, as prose
            (TFeLwOAS1EeP). No tile: the tiles were dead ends. */}
        {/* Commented out on Jonathan's day plan 2026-10-08 — budget, inbox and
            calendar line retired from Life. Re-enable by uncommenting. */}
        {/* <p className="life-soon">
          Budget, inbox and calendar are still to come.
        </p> */}
      </SubPanel>

    </>
  );
}
